// src/pages/SignUpPage.js

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// --- Objeto de Traducciones ---
const translations = {
  en: {
    languageLabel: 'Language',
    successTitle: 'Registration Successful!',
    successLine1: 'We have sent a confirmation email to your address.',
    successLine2: 'Please click the link in the email to activate your account.',
    goToLogin: 'Got it, go to Login',
    createAccount: 'Create Account',
    joinCommunity: 'Join the community and start your transformation.',
    usernameLabel: 'Username *',
    nickLabel: 'Nick *',
    nameLabel: 'Name (Optional)',
    emailLabel: 'Email Address *',
    passwordLabel: 'Password *',
    confirmPasswordLabel: 'Confirm Password *',
    registering: 'Registering...',
    createMyAccount: 'Create my Account',
    alreadyHaveAccount: 'Already have an account?',
    logIn: 'Log in',
    fillAllFields: 'Please complete all required fields.',
    passwordsDoNotMatch: 'Passwords do not match.',
    passwordTooShort: 'Password must be at least 8 characters long.',
    invalidUsername: 'Username can only contain letters, numbers, and underscores.',
  },
  es: {
    languageLabel: 'Idioma',
    successTitle: '¡Registro Exitoso!',
    successLine1: 'Hemos enviado un correo de confirmación a tu dirección de email.',
    successLine2: 'Por favor, haz clic en el enlace del correo para activar tu cuenta.',
    goToLogin: 'Entendido, ir a Iniciar Sesión',
    createAccount: 'Crear Cuenta',
    joinCommunity: 'Únete a la comunidad y comienza tu transformación.',
    usernameLabel: 'Nombre de Usuario *',
    nickLabel: 'Nick *',
    nameLabel: 'Nombre (Opcional)',
    emailLabel: 'Correo Electrónico *',
    passwordLabel: 'Contraseña *',
    confirmPasswordLabel: 'Confirmar Contraseña *',
    registering: 'Registrando...',
    createMyAccount: 'Crear mi Cuenta',
    alreadyHaveAccount: '¿Ya tienes una cuenta?',
    logIn: 'Inicia sesión',
    fillAllFields: 'Por favor, completa todos los campos obligatorios.',
    passwordsDoNotMatch: 'Las contraseñas no coinciden.',
    passwordTooShort: 'La contraseña debe tener al menos 8 caracteres.',
    invalidUsername: 'El nombre de usuario solo puede contener letras, números y guiones bajos.',
  },
};

const SuccessMessage = ({ t, lang }) => (
  <div className="text-center">
    <h1 className="text-3xl font-bold text-green-400">{t.successTitle}</h1>
    <p className="text-gray-300 mt-4">{t.successLine1}</p>
    <p className="text-gray-400 mt-2">{t.successLine2}</p>
    <Link to={`/${lang}/login`} className="mt-8 inline-block w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 px-4 rounded-lg">
      {t.goToLogin}
    </Link>
  </div>
);

const SignUpPage = () => {
  const { lang } = useParams();
  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();
  const { signup } = useAuth();

  // Aseguramos que siempre sea 'es' o 'en'
  const currentLang = lang === 'en' ? 'en' : 'es';
  const t = translations[currentLang];

  const [formData, setFormData] = useState({
    username: '', nick: '', name: '', email: '', password: '', confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Soporte para query param ?lang=en o ?lang=es si alguien entra por esa URL
  useEffect(() => {
    const queryLang = searchParams.get('lang');
    if (queryLang === 'es' || queryLang === 'en') {
      navigate(`/${queryLang}/signup`, { replace: true });
    }
  }, [searchParams, navigate]);

  // Manejo de cambio en el selector de idioma
  const handleLanguageChange = (e) => {
    const selectedLang = e.target.value;
    navigate(`/${selectedLang}/signup`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'username') {
      newValue = newValue.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
    }
    
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { username, nick, name, email, password, confirmPassword } = formData;

    setError('');

    if (!username || !nick || !email || !password || !confirmPassword) {
      setError(t.fillAllFields);
      return;
    }
    if (password !== confirmPassword) {
      setError(t.passwordsDoNotMatch);
      return;
    }
    if (password.length < 8) {
      setError(t.passwordTooShort);
      return;
    }
    if (username !== username.replace(/[^a-z0-9_]/g, '')) {
      setError(t.invalidUsername);
      return;
    }
    
    setIsLoading(true);

    try {
      // Se envía currentLang ('es' o 'en') al registrar
      await signup(username, nick, name, email, password, currentLang);
      setIsSuccess(true); 
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center items-center p-4">
      <div className="w-full max-w-md bg-gray-900/50 border border-yellow-500/20 rounded-xl p-8 shadow-2xl shadow-yellow-500/10">
        
        {/* Selector de idioma */}
        <div className="flex justify-end items-center mb-4">
          <label htmlFor="lang-select" className="text-xs text-gray-400 mr-2">
            {t.languageLabel}:
          </label>
          <select
            id="lang-select"
            value={currentLang}
            onChange={handleLanguageChange}
            className="bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded-md px-2 py-1 focus:outline-none focus:border-yellow-500 cursor-pointer"
          >
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
        </div>

        {isSuccess ? (
          <SuccessMessage t={t} lang={currentLang} />
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-yellow-400">{t.createAccount}</h1>
              <p className="text-gray-400 mt-2">{t.joinCommunity}</p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="username">{t.usernameLabel}</label>
                  <input id="username" name="username" type="text" value={formData.username} onChange={handleChange} required className="w-full bg-gray-800 border-gray-700 rounded-lg p-2 text-white focus:outline-none focus:border-yellow-500" />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="nick">{t.nickLabel}</label>
                  <input id="nick" name="nick" type="text" value={formData.nick} onChange={handleChange} required className="w-full bg-gray-800 border-gray-700 rounded-lg p-2 text-white focus:outline-none focus:border-yellow-500" />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="name">{t.nameLabel}</label>
                <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} className="w-full bg-gray-800 border-gray-700 rounded-lg p-2 text-white focus:outline-none focus:border-yellow-500" />
              </div>
              <div className="mb-4">
                <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="email">{t.emailLabel}</label>
                <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="w-full bg-gray-800 border-gray-700 rounded-lg p-2 text-white focus:outline-none focus:border-yellow-500" />
              </div>
              <div className="mb-4">
                <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="password">{t.passwordLabel}</label>
                <input id="password" name="password" type="password" value={formData.password} onChange={handleChange} required className="w-full bg-gray-800 border-gray-700 rounded-lg p-2 text-white focus:outline-none focus:border-yellow-500" />
              </div>
              <div className="mb-6">
                <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="confirmPassword">{t.confirmPasswordLabel}</label>
                <input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} required className="w-full bg-gray-800 border-gray-700 rounded-lg p-2 text-white focus:outline-none focus:border-yellow-500" />
              </div>

              {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}

              <button type="submit" disabled={isLoading} className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 px-4 rounded-lg disabled:bg-gray-600">
                {isLoading ? t.registering : t.createMyAccount}
              </button>

              <p className="text-center text-sm text-gray-500 mt-6">
                {t.alreadyHaveAccount}{' '}
                <Link to={`/${currentLang}/login`} className="font-bold text-yellow-400 hover:text-yellow-300">
                  {t.logIn}
                </Link>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default SignUpPage;